// Single source of truth for authorization decisions (role checks, capability checks,
// and "what can this caller grant/see" filtering). See PR-1681-remaining-issues.md.

import { prop } from 'ramda'
import { RoleScope } from 'account/components/userManagement/roles/model'
import type { CqrsCapability, CqrsCapabilityPermission } from 'api-client/keystone.model'
import store from 'app/store'
import { sessionStoreKey } from 'core/session/sessionReducers'

// Inlined rather than imported from api-client/helpers to avoid a module
// cycle (api-client/helpers -> ApiClient -> ... -> core/utils/rbac).
const getDomainId = () => store.getState()?.session?.domain?.id || 'default'

// Defined here rather than imported from userManagement/roles/selectors to avoid a module
// cycle (that module imports filterCapabilitiesForUser from here).
export const legacyRoles = [
  'admin',
  'member',
  'reader',
  'service',
  'manager',
  'load-balancer_global_observer',
  'load-balancer_member',
  'load-balancer_observer',
  'load-balancer_quota_admin',
  'load-balancer_admin',
]

export enum UserRoles {
  Admin = 'admin',
  Member = 'member',
  Reader = 'reader',
  // TODO figure out what other roles we have for users
}

export const getCurrentUser = () => {
  const state = store.getState()
  const session = state[sessionStoreKey]
  return prop('userDetails', session)
}

export const getUserRole = () => getCurrentUser()?.role

export const userHasRole = (roles: string[]) => {
  const user = getCurrentUser()
  if (!user) {
    return false
  }
  return roles.includes(user.role)
}

export const isDefaultDomain = (domain?: string) => {
  return (
    !domain ||
    domain.trim() === '' ||
    domain.trim() === 'Default Domain' ||
    domain?.trim()?.toLowerCase() === 'default'
  )
}

export const isSsoUser = () => {
  const state = store.getState()
  const session = state[sessionStoreKey]
  return session?.isSsoToken
}

// Broad admin concept: any role with system/domain-wide administrative authority.
export const isAdminRole = (providedSession = undefined) => {
  const state = store.getState()
  const session = providedSession ?? state[sessionStoreKey]
  const user = prop('userDetails', session)
  if (!user) {
    return false
  }
  return [
    UserRoles.Admin,
    'cluster_admin',
    'platform_admin',
    'customer_domain_admin',
    'sp_admin',
  ].includes(user.role)
}

export const isSelfServiceRole = (providedSession = undefined) => {
  const state = store.getState()
  const session = providedSession ?? state[sessionStoreKey]
  const user = prop('userDetails', session)
  if (!user) {
    return false
  }
  return user.role === UserRoles.Member
}

export const isReaderRole = (providedSession = undefined) => {
  const state = store.getState()
  const session = providedSession ?? state[sessionStoreKey]
  const user = prop('userDetails', session)
  if (!user) {
    return false
  }
  return user.role === UserRoles.Reader
}

export const isLegacyNonAdminRole = () => {
  return isSelfServiceRole() || isReaderRole()
}

export const isLegacyRole = () => {
  const state = store.getState()
  const session = state[sessionStoreKey]
  const user = prop('userDetails', session)
  if (!user) {
    return false
  }
  return legacyRoles.includes(user.role)
}

export const isRestrictedSsu = () => {
  const state = store.getState()
  const session = state[sessionStoreKey]
  return session?.features?.features?.restricted_ssu && isSelfServiceRole()
}

// Legacy admin user from the default domain is a system admin
export const isSystemAdminUser = () =>
  (userHasRole(['admin']) && isDefaultDomain(getDomainId())) ||
  userHasRole(['platform_admin', 'sp_admin'])

// Named authorization predicates — replaces repeated `userHasRole([...])` literal
// arrays scattered across userManagement components (PR-1681-remaining-issues.md §6).
export const canManageSystemRoles = () => isSystemAdminUser() && !userHasRole(['sp_admin'])

// Different from canManageDomains (can manage resources across domains)
export const canManageAllDomains = () => isSystemAdminUser()

export const canManageDomainRoles = () =>
  userHasRole(['admin', 'platform_admin', 'customer_domain_admin'])

// Different from canManageAllDomains (can actually CRUD on domains)
export const canManageDomains = () => userHasRole(['admin', 'platform_admin'])

export const canManageTenantsAndUsers = () => userHasRole(['admin', 'platform_admin', 'sp_admin'])

export const isCustomerDomainAdmin = () => userHasRole(['customer_domain_admin'])

export const canViewManagementUsers = () => isAdminRole() || userHasRole(['tenant_admin'])

export const canManageInfrastructure = () =>
  userHasRole(['admin', 'platform_admin', 'cluster_admin'])

export const canManageOtherSshKeys = () => userHasRole(['admin', 'platform_admin', 'sp_admin'])

export const canManageExternalNetworks = () => userHasRole(['admin', 'platform_admin', 'sp_admin'])

// Only show roles the caller is themselves permitted to grant (equal-or-lower ceiling).
export const dropdownRolesForUser = (userRole: string, role: { name: string }) => {
  if (['admin', 'platform_admin'].includes(userRole)) {
    return true
  } else if (userRole === 'sp_admin') {
    return !['admin', 'platform_admin'].includes(role.name)
  } else if (userRole === 'customer_domain_admin') {
    return !['admin', 'platform_admin', 'sp_admin', 'cluster_admin'].includes(role.name)
  } else if (userRole === 'tenant_admin') {
    return ![
      'admin',
      'platform_admin',
      'sp_admin',
      'cluster_admin',
      'customer_domain_admin',
    ].includes(role.name)
  }
  return false
}

// Only show roles valid for the given assignment scope (system/domain/tenant).
export const dropdownRolesForContext = (
  context: string,
  role: { name: string },
  predefinedDomainRoles: string[],
  predefinedSystemRoles: string[],
) => {
  if (context === RoleScope.Tenant) {
    return ![...predefinedDomainRoles, ...predefinedSystemRoles].includes(role.name)
  } else if (context === RoleScope.Domain) {
    return predefinedDomainRoles.includes(role.name)
  } else if (context === RoleScope.System) {
    return predefinedSystemRoles.includes(role.name)
  }
  return true
}

// Unlike roles, the capability/permission catalog returned by `getCapabilitiesList()` carries
// no explicit tier/scope field to key a ceiling off of — each permission is just an
// OpenStack/Keystone policy id (e.g. `identity.create_domain`) with no "minimum role" metadata.
// As a conservative proxy for "platform_admin-only" until the backend exposes a real tier,
// treat Keystone identity actions that manage domains/services/endpoints/regions/policies as
// system-tier-exclusive — these are cloud-admin-only concepts in OpenStack today.
const SYSTEM_ONLY_PERMISSION_KEYWORDS = [
  'create_domain',
  'update_domain',
  'delete_domain',
  'create_endpoint',
  'update_endpoint',
  'delete_endpoint',
  'create_service',
  'update_service',
  'delete_service',
  'create_region',
  'update_region',
  'delete_region',
  'create_policy',
  'update_policy',
  'delete_policy',
]

const isSystemOnlyPermission = (permission: CqrsCapabilityPermission) =>
  SYSTEM_ONLY_PERMISSION_KEYWORDS.some((keyword) => permission.id.includes(keyword))

// Only offer capabilities/permissions the caller is themselves permitted to grant — same
// equal-or-lower ceiling concept as `dropdownRolesForUser`, applied to the capability catalog
// used to assemble a custom role (`CreateCustomRolePermissionsStep`). Top-tier callers see the
// catalog unfiltered; everyone else has system-tier-only permissions stripped out of each
// capability. See SYSTEM_ONLY_PERMISSION_KEYWORDS above for the caveat on how "tier" is
// approximated here.
export const filterCapabilitiesForUser = (
  userRole: string,
  capability: CqrsCapability,
): CqrsCapability => {
  if (['admin', 'platform_admin'].includes(userRole)) {
    return capability
  }
  return {
    ...capability,
    permissions: capability.permissions.filter((permission) => !isSystemOnlyPermission(permission)),
  }
}
